import type { CSSProperties } from 'react';

import type { TypographyProps } from '../components/typography/typography';
import type { Spacing } from '../utils/spacing.types';
import type { BorderRadiusKey } from './theme.types';
import type { ColorKey } from './themeColors.types';

/** components.Icon Types */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';

type IconSizeConfig = {
  [key in IconSize]: string;
};

interface IconConfig {
  sizes: IconSizeConfig;
}

/** components.Typography Types */

type TypographyComponent =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'div'
  | 'span'
  | 'label'
  | 'legend';

// LEGACY VARIANTS
// TODO(Typography): Remove these once new variants are rolled out
const LEGACY_BASE_TYPOGRAPHY_VARIANTS = [
  'title',
  'header',
  'body',
  'mini',
  'caps',
  'huge',
  'section',
] as const;

const LEGACY_BOLD_TYPOGRAPHY_VARIANTS = [
  'title-bold',
  'header-bold',
  'body-bold',
  'mini-bold',
  'caps-bold',
  'huge-bold',
  'section-bold',
] as const;

export const LEGACY_TYPOGRAPHY_VARIANTS = [
  ...LEGACY_BASE_TYPOGRAPHY_VARIANTS,
  ...LEGACY_BOLD_TYPOGRAPHY_VARIANTS,
] as const;
// ------------------------------------------------

// NEW VARIANTS
const BASE_TYPOGRAPHY_VARIANTS = [
  'h1-regular',
  'h2-regular',
  'h3-regular',
  'h4-regular',
  'body-regular',
  'small-body-regular',
  'caps-regular',
] as const;

const BOLD_TYPOGRAPHY_VARIANTS = [
  'h1-semibold',
  'h2-semibold',
  'h3-semibold',
  'h4-semibold',
  'body-semibold',
  'small-body-semibold',
  'caps-semibold',
] as const;

// TODO(Typography): remove this once new variants are rolled out
// (cause then it'll be the same as TYPOGRAPHY_VARIANTS)
export const NEW_TYPOGRAPHY_VARIANTS = [
  ...BASE_TYPOGRAPHY_VARIANTS,
  ...BOLD_TYPOGRAPHY_VARIANTS,
] as const;

export const TYPOGRAPHY_VARIANTS = [
  ...LEGACY_BASE_TYPOGRAPHY_VARIANTS,
  ...LEGACY_BOLD_TYPOGRAPHY_VARIANTS,
  ...BASE_TYPOGRAPHY_VARIANTS,
  ...BOLD_TYPOGRAPHY_VARIANTS,
] as const;

export interface Font {
  fontFamily?: CSSProperties['fontFamily'];
  url?: string;
}
interface TypographyFonts {
  branded: Font;
  default: Font;
}

type InnerTypographyVariantConfigOptions = Pick<
  CSSProperties,
  'fontSize' | 'fontWeight' | 'lineHeight' | 'textTransform' | 'letterSpacing'
>;

type SharedTypographyVariantConfig =
  Partial<InnerTypographyVariantConfigOptions>;

type TypographyVariantConfigOptions = Pick<TypographyProps, 'branded'> &
  Required<SharedTypographyVariantConfig> & {
    desktopStyles?: SharedTypographyVariantConfig;
    mobileStyles?: SharedTypographyVariantConfig;
  };

export type TypographyVariantConfig<T extends string = TypographyVariant> = {
  [key in T]: TypographyVariantConfigOptions;
};

// TODO(Typography): Uncomment these once new variants are rolled out
// export type BaseTypographyVariantConfig =
//   TypographyVariantConfig<BaseTypographyVariant>;

// export type BoldTypographyVariantConfig =
//   TypographyVariantConfig<BoldTypographyVariant>;

// export type BoldTypographyVariant = (typeof BOLD_TYPOGRAPHY_VARIANTS)[number];
// export type BaseTypographyVariant = (typeof BASE_TYPOGRAPHY_VARIANTS)[number];

export type TypographyVariant = (typeof TYPOGRAPHY_VARIANTS)[number];

// TODO(Typography): Uncomment once new variants are rolled out
// export type TypographyVariantComponentMap<
type TypographyVariantComponentMap<T extends string = TypographyVariant> = {
  [variant in T]: TypographyComponent;
};

// TODO(Typography): Remove legacy types once new variants are rolled out
export type LegacyBoldTypographyVariant =
  (typeof LEGACY_BOLD_TYPOGRAPHY_VARIANTS)[number];
export type LegacyBaseTypographyVariant =
  (typeof LEGACY_BASE_TYPOGRAPHY_VARIANTS)[number];
export type LegacyTypographyVariant =
  (typeof LEGACY_TYPOGRAPHY_VARIANTS)[number];

export type LegacyTypographyVariantConfig<
  T extends string = LegacyTypographyVariant,
> = {
  [key in T]: TypographyVariantConfigOptions;
};
export type LegacyBaseTypographyVariantConfig =
  LegacyTypographyVariantConfig<LegacyBaseTypographyVariant>;
export type LegacyBoldTypographyVariantConfig =
  LegacyTypographyVariantConfig<LegacyBoldTypographyVariant>;

export type LegacyTypographyVariantComponentMap<
  T extends string = LegacyTypographyVariant,
> = {
  [variant in T]: TypographyComponent;
};
// ------------------------------------------------

export type TypographyConfig<
  Variants extends string = TypographyVariant,
  Map extends string = TypographyVariant,
> = {
  componentMapping: TypographyVariantComponentMap<Map>;
  fonts: TypographyFonts;
  variants: TypographyVariantConfig<Variants>;
};

/** components.Button Types */

const _BUTTON_VARIANTS = [
  'primary',
  'primary-inverse',
  'secondary',
  'tertiary',
  'tertiary-selected',
  'destructive',
  'destructive-secondary',
  'minimal',
  'minimal-inverse',
  'gradient-primary',
] as const;

const _BUTTON_SIZES = ['small', 'default', 'large', 'jumbo'] as const;

export type ButtonSize = (typeof _BUTTON_SIZES)[number];

export type ButtonVariant = (typeof _BUTTON_VARIANTS)[number];

interface ButtonSharedConfig {
  borderRadius?: BorderRadiusKey;
  borderWidth?: string;
  typographyVariant?: TypographyVariant;
}

export interface ButtonSizeConfig {
  borderRadius?: BorderRadiusKey;
  height?: number | string;
  paddingX?: Spacing;
  typographyVariant?: TypographyVariant;
}

export interface ButtonVariantConfig {
  backgroundColor?: ColorKey;
  borderColor?: ColorKey;
  borderWidth?: string;
  typographyColor?: ColorKey;
  typographyVariant?: TypographyVariant;
}

/** components types */
export interface ComponentsConfig<
  Variant extends string = string,
  ComponentMap extends string = string,
> {
  Button: {
    icons: {
      sizes: {
        [key in ButtonSize]: ButtonSizeConfig;
      };
    };
    shared: ButtonSharedConfig;
    sizes: {
      [key in ButtonSize]: ButtonSizeConfig;
    };
    variants: {
      [key in ButtonVariant]: ButtonVariantConfig;
    };
  };

  Card: {
    styles: {
      borderRadius: BorderRadiusKey;
    };
  };

  Icon: IconConfig;

  Typography: TypographyConfig<Variant, ComponentMap>;
}
